/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, LogOut, Star, Flame, BookOpen, Trophy, Target, Clock, Rocket, HandHeart, Headphones, Hammer, ChevronRight, TrendingUp, Award, Calendar, Users, Zap, ArrowUpRight, CheckCircle, Circle, BarChart3, MessageSquare, Pin, User, Plus, Edit3, Trash2, CheckCircle2 } from 'lucide-react';
import { checkAuth, logout } from '../auth/config';
import NavigationModal from '../components/NavigationModal';
import NavigationCarousel from '../components/NavigationCarousel';
import { useTheme } from '../contexts/ThemeContext';
import DashboardHeader from '../components/DashboardHeader';
import { 
  BarChart, Bar, LineChart, Line, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';

const Progress = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('week');
  const [todos, setTodos] = useState([
    { id: 1, text: 'Complete Spatial Reasoning Fundamentals course', completed: false, createdAt: new Date() },
    { id: 2, text: 'Submit BIM Applications assignment', completed: false, createdAt: new Date() },
    { id: 3, text: 'Review sustainability design principles', completed: true, createdAt: new Date() },
    { id: 4, text: 'Prepare for NCARB mock exam', completed: false, createdAt: new Date() }
  ]);
  const [newTodo, setNewTodo] = useState('');
  const [editingTodo, setEditingTodo] = useState(null);
  const [editText, setEditText] = useState('');
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  
  // Check for signup user first, then demo user as fallback
  const signupUser = JSON.parse(localStorage.getItem('user') || 'null');
  const demoUser = checkAuth();
  const user = signupUser || demoUser;
  
  // Handle sidebar toggle
  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  // Handle navigation carousel clicks
  const handleNavigationClick = (item) => {
    if (item.path === '/login') {
      // Handle logout
      logout();
      localStorage.removeItem('user');
      localStorage.removeItem('isLoggedIn');
      localStorage.removeItem('onboardingComplete');
      navigate('/login');
    } else {
      navigate(item.path);
    }
  };

  // Todo management functions
  const addTodo = () => {
    if (newTodo.trim()) {
      const newTodoItem = {
        id: Date.now(),
        text: newTodo.trim(),
        completed: false,
        createdAt: new Date()
      };
      setTodos([...todos, newTodoItem]);
      setNewTodo('');
    }
  };

  const deleteTodo = (id) => {
    setTodos(todos.filter(todo => todo.id !== id));
  };

  const startEdit = (todo) => {
    setEditingTodo(todo.id);
    setEditText(todo.text);
  };

  const saveEdit = () => {
    if (editText.trim()) {
      setTodos(todos.map(todo => 
        todo.id === editingTodo 
          ? { ...todo, text: editText.trim() }
          : todo
      ));
      setEditingTodo(null);
      setEditText('');
    }
  };

  const cancelEdit = () => {
    setEditingTodo(null);
    setEditText('');
  };

  // AI completion logic - simulates AI deciding when to mark items as complete
  const checkAICompletion = (todo) => {
    // Simulate AI analysis based on various factors
    const completionKeywords = ['complete', 'finished', 'done', 'submitted', 'passed', 'achieved'];
    const hasCompletionKeyword = completionKeywords.some(keyword => 
      todo.text.toLowerCase().includes(keyword)
    );
    
    // Simulate AI checking progress data, course completion, etc.
    const randomCompletion = Math.random() < 0.1; // 10% chance per check
    
    return hasCompletionKeyword || randomCompletion;
  };

  // Simulate AI periodically checking and updating todo completion
  React.useEffect(() => {
    const interval = setInterval(() => {
      setTodos(prevTodos => 
        prevTodos.map(todo => {
          if (!todo.completed && checkAICompletion(todo)) {
            return { ...todo, completed: true };
          }
          return todo;
        })
      );
    }, 10000); // Check every 10 seconds

    return () => clearInterval(interval);
  }, []);

  // Mouse tracking for interactive glow effect
  React.useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Dynamic progress data based on selected period
  const getProgressDataForPeriod = (period) => {
    const progressData = {
      week: [
        { day: 'Mon', studyHours: 2.5, previousWeek: 2.1 },
        { day: 'Tue', studyHours: 3.2, previousWeek: 2.8 },
        { day: 'Wed', studyHours: 1.8, previousWeek: 2.3 },
        { day: 'Thu', studyHours: 4.1, previousWeek: 3.5 },
        { day: 'Fri', studyHours: 2.9, previousWeek: 2.7 },
        { day: 'Sat', studyHours: 3.8, previousWeek: 3.2 },
        { day: 'Sun', studyHours: 2.2, previousWeek: 1.9 }
      ],
      month: [
        { week: 'Week 1', studyHours: 18.5, previousMonth: 16.2 },
        { week: 'Week 2', studyHours: 22.3, previousMonth: 19.8 },
        { week: 'Week 3', studyHours: 19.7, previousMonth: 21.1 },
        { week: 'Week 4', studyHours: 25.1, previousMonth: 18.9 }
      ],
      semester: [
        { month: 'Jan', studyHours: 78.2, previousSemester: 72.5 },
        { month: 'Feb', studyHours: 85.4, previousSemester: 79.1 },
        { month: 'Mar', studyHours: 92.1, previousSemester: 88.3 },
        { month: 'Apr', studyHours: 88.7, previousSemester: 85.6 },
        { month: 'May', studyHours: 95.3, previousSemester: 91.2 },
        { month: 'Jun', studyHours: 82.9, previousSemester: 78.4 }
      ]
    };
    
    return progressData[period] || progressData.week;
  };

  const progressData = getProgressDataForPeriod(selectedPeriod);

  const recentAchievements = [
    { title: 'Academic Weapon', description: 'Studied 4 hours on 11th', icon: BookOpen },
    { title: 'Networking Warrior', description: 'Interacted with 3 new contacts in week 6', icon: Users }
  ];

  // Dynamic metric cards data based on selected period
  const getMetricCardsForPeriod = (period) => {
    const metricCardsData = {
      week: [
    {
      title: 'STUDY',
      value: 78,
      icon: BookOpen,
      sections: {
        Resources: [
          { name: 'Modern Communication.', hours: 12.5, maxHours: 15 },
          { name: 'Is for Architecture Pr.', hours: 8.2, maxHours: 12 },
          { name: 'Civil Engineering Const.', hours: 15.8, maxHours: 18 }
        ],
        Courses: [
          { name: 'Intro to Spatial Reason.', hours: 4.5, maxHours: 20 },
          { name: 'BIM Appli. GIS Mappin.', hours: 8.0, maxHours: 16 },
          { name: 'Intro to Sustainability.', hours: 11.2, maxHours: 16 },
          { name: 'What Happens After C.', hours: 13.4, maxHours: 16 }
        ],
        Tests: [
          { name: 'Spatial Processing 250', attempts: 3, maxAttempts: 5 },
          { name: 'NCARB/U.R.E. Mock Te.', attempts: 10, maxAttempts: 15 }
        ]
      }
    },
    {
      title: 'CHALLENGES',
      value: 34,
      icon: Trophy,
      sections: {
        Local: [
          { name: 'Green Tech Park Design.', hours: 6.8, maxHours: 20 },
          { name: 'Smart City Infrastructu.', hours: 4.2, maxHours: 18 },
          { name: 'Community Center Ren.', hours: 7.1, maxHours: 22 }
        ]
      }
    },
    {
      title: 'COMMUNITY',
      value: 66,
      icon: Users,
      sections: {
        Discussions: [
          { name: 'Struggling with BIM A.', hours: 3.2, maxHours: 10 },
          { name: 'Study materials for Spa.', hours: 2.1, maxHours: 8 },
          { name: 'Sustainability Design P.', hours: 4.5, maxHours: 12 }
        ],
        Courses: [
          { name: 'Intro to Sustainable.', hours: 5.8, maxHours: 16 },
          { name: 'Spatial Reasoning Fund.', hours: 3.9, maxHours: 16 },
          { name: 'BIM Applications. GIS.', hours: 6.2, maxHours: 20 }
        ]
      }
    },
    {
      title: 'PINUP',
      value: 22,
      icon: Pin,
      sections: {
        'Course Projects': [
          { name: 'Intro to Spatial Reason.', hours: 4.4, maxHours: 20 }
        ]
      }
    }
      ],
      month: [
        {
          title: 'STUDY',
          value: 85,
          icon: BookOpen,
          sections: {
            Resources: [
              { name: 'Modern Communication.', hours: 45.2, maxHours: 60 },
              { name: 'Is for Architecture Pr.', hours: 38.7, maxHours: 48 },
              { name: 'Civil Engineering Const.', hours: 52.1, maxHours: 72 }
            ],
            Courses: [
              { name: 'Intro to Spatial Reason.', hours: 18.5, maxHours: 80 },
              { name: 'BIM Appli. GIS Mappin.', hours: 32.0, maxHours: 64 },
              { name: 'Intro to Sustainability.', hours: 44.8, maxHours: 64 },
              { name: 'What Happens After C.', hours: 53.6, maxHours: 64 }
            ],
            Tests: [
              { name: 'Spatial Processing 250', attempts: 12, maxAttempts: 20 },
              { name: 'NCARB/U.R.E. Mock Te.', attempts: 40, maxAttempts: 60 }
            ]
          }
        },
        {
          title: 'CHALLENGES',
          value: 67,
          icon: Trophy,
          sections: {
            Local: [
              { name: 'Green Tech Park Design.', hours: 27.2, maxHours: 80 },
              { name: 'Smart City Infrastructu.', hours: 16.8, maxHours: 72 },
              { name: 'Community Center Ren.', hours: 28.4, maxHours: 88 }
            ]
          }
        },
        {
          title: 'COMMUNITY',
          value: 78,
          icon: Users,
          sections: {
            Discussions: [
              { name: 'Struggling with BIM A.', hours: 12.8, maxHours: 40 },
              { name: 'Study materials for Spa.', hours: 8.4, maxHours: 32 },
              { name: 'Sustainability Design P.', hours: 18.0, maxHours: 48 }
            ],
            Courses: [
              { name: 'Intro to Sustainable.', hours: 23.2, maxHours: 64 },
              { name: 'Spatial Reasoning Fund.', hours: 15.6, maxHours: 64 },
              { name: 'BIM Applications. GIS.', hours: 24.8, maxHours: 80 }
            ]
          }
        },
        {
          title: 'PINUP',
          value: 45,
          icon: Pin,
          sections: {
            'Course Projects': [
              { name: 'Intro to Spatial Reason.', hours: 17.6, maxHours: 80 }
            ]
          }
        }
      ],
      semester: [
        {
          title: 'STUDY',
          value: 92,
          icon: BookOpen,
          sections: {
            Resources: [
              { name: 'Modern Communication.', hours: 180.8, maxHours: 240 },
              { name: 'Is for Architecture Pr.', hours: 154.8, maxHours: 192 },
              { name: 'Civil Engineering Const.', hours: 208.4, maxHours: 288 }
            ],
            Courses: [
              { name: 'Intro to Spatial Reason.', hours: 74.0, maxHours: 320 },
              { name: 'BIM Appli. GIS Mappin.', hours: 128.0, maxHours: 256 },
              { name: 'Intro to Sustainability.', hours: 179.2, maxHours: 256 },
              { name: 'What Happens After C.', hours: 214.4, maxHours: 256 }
            ],
            Tests: [
              { name: 'Spatial Processing 250', attempts: 48, maxAttempts: 80 },
              { name: 'NCARB/U.R.E. Mock Te.', attempts: 160, maxAttempts: 240 }
            ]
          }
        },
        {
          title: 'CHALLENGES',
          value: 88,
          icon: Trophy,
          sections: {
            Local: [
              { name: 'Green Tech Park Design.', hours: 108.8, maxHours: 320 },
              { name: 'Smart City Infrastructu.', hours: 67.2, maxHours: 288 },
              { name: 'Community Center Ren.', hours: 113.6, maxHours: 352 }
            ]
          }
        },
        {
          title: 'COMMUNITY',
          value: 91,
          icon: Users,
          sections: {
            Discussions: [
              { name: 'Struggling with BIM A.', hours: 51.2, maxHours: 160 },
              { name: 'Study materials for Spa.', hours: 33.6, maxHours: 128 },
              { name: 'Sustainability Design P.', hours: 72.0, maxHours: 192 }
            ],
            Courses: [
              { name: 'Intro to Sustainable.', hours: 92.8, maxHours: 256 },
              { name: 'Spatial Reasoning Fund.', hours: 62.4, maxHours: 256 },
              { name: 'BIM Applications. GIS.', hours: 99.2, maxHours: 320 }
            ]
          }
        },
        {
          title: 'PINUP',
          value: 76,
          icon: Pin,
          sections: {
            'Course Projects': [
              { name: 'Intro to Spatial Reason.', hours: 70.4, maxHours: 320 }
            ]
          }
        }
      ]
    };
    
    return metricCardsData[period] || metricCardsData.week;
  };

  const metricCards = getMetricCardsForPeriod(selectedPeriod);

  const recentActivity = [
    { action: 'Completed "Spatial Reasoning Fundamentals"', time: '2 hours ago', type: 'course' },
    { action: 'Won "Green Tech Park Design" challenge', time: '1 day ago', type: 'challenge' },
    { action: 'Joined "Architecture Fundamentals" study group', time: '2 days ago', type: 'community' },
    { action: 'Earned "Study Streak" achievement', time: '3 days ago', type: 'achievement' }
  ];


  const getRarityColor = (rarity) => {
    switch (rarity) {
      case 'legendary': return 'from-yellow-400 to-orange-500';
      case 'epic': return 'from-purple-400 to-pink-500';
      case 'rare': return 'from-blue-400 to-cyan-500';
      case 'common': return 'from-gray-400 to-gray-500';
      default: return 'from-gray-400 to-gray-500';
    }
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
      {/* Fixed Navigation Overlay */}
      <div className="fixed top-0 left-0 z-50" style={{ zIndex: 60 }}>
        <NavigationCarousel
          menuItems={[
            { label: 'HOME', path: '/app' },
            { label: 'STUDY', path: '/app/study' },
            { label: 'CHALLENGES', path: '/app/challenges' },
            { label: 'COMMUNITY', path: '/app/community' },
            { label: 'PIN UP', path: '/app/pinup' },
            { label: 'PROGRESS', path: '/app/progress' },
            { label: 'PROFILE', path: '/app/profile' },
            { label: 'LOG OUT', path: '/login', isLogout: true }
          ]}
          onItemClick={handleNavigationClick}
          isCollapsed={isSidebarCollapsed}
          onToggleSidebar={toggleSidebar}
        />
      </div>

      {/* Student Dashboard Header */}
      <DashboardHeader title="PROGRESS" isSidebarCollapsed={isSidebarCollapsed} />

      <div className="max-w-7xl mx-auto px-6 py-8" style={{ 
        marginLeft: isSidebarCollapsed ? '90px' : '14vw',
        transition: 'margin-left 0.3s ease'
      }}>
        {/* Control Bar */}
        <div className="mb-8">
          <div className={`rounded-2xl p-6 border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Progress Dashboard</h2>
              
              <div className="flex gap-3 flex-wrap">
                {/* Period Filter */}
                <div className="flex items-center gap-2">
                  <label className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Filter:</label>
                  <select
                    value={selectedPeriod}
                    onChange={(e) => setSelectedPeriod(e.target.value)}
                    className={`px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#AC5757] focus:border-transparent text-sm ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    <option value="week">Week</option>
                    <option value="month">Month</option>
                    <option value="semester">Semester</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid - Wireframe Layout */}
        <div className="space-y-8">
          {/* Top Section - Graph and Four Metric Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Left Column - Progress Overview Graph */}
            <div className="lg:col-span-2">
              <h3 className={`text-2xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {selectedPeriod === 'week' ? 'Weekly' : selectedPeriod === 'month' ? 'Monthly' : 'Semester'} Study Progress
              </h3>
              <div className={`rounded-lg p-6 shadow-sm border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={progressData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? "#374151" : "#E5E7EB"} />
                    <XAxis 
                      dataKey={selectedPeriod === 'week' ? 'day' : selectedPeriod === 'month' ? 'week' : 'month'} 
                      stroke={isDarkMode ? "#9CA3AF" : "#6B7280"} 
                    />
                    <YAxis stroke={isDarkMode ? "#9CA3AF" : "#6B7280"} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: isDarkMode ? '#374151' : '#fff', 
                        border: isDarkMode ? '1px solid #4B5563' : '1px solid #E5E7EB',
                        borderRadius: '8px',
                        color: isDarkMode ? '#F9FAFB' : '#111827'
                      }} 
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey={selectedPeriod === 'week' ? 'previousWeek' : selectedPeriod === 'month' ? 'previousMonth' : 'previousSemester'}
                      stroke={isDarkMode ? "#6B7280" : "#E5E7EB"} 
                      strokeWidth={2}
                      dot={{ fill: isDarkMode ? "#6B7280" : "#E5E7EB", r: 4 }}
                      name={selectedPeriod === 'week' ? 'Previous Week' : selectedPeriod === 'month' ? 'Previous Month' : 'Previous Semester'}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="studyHours" 
                        stroke="#AC5757"
                      strokeWidth={3}
                      dot={{ fill: '#AC5757', r: 5 }}
                      name="Study Hours"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Right Columns - Four Metric Cards */}
            <div className="lg:col-span-3 grid grid-cols-2 lg:grid-cols-4 gap-4">
              {metricCards.map((card, idx) => {
                const Icon = card.icon;
                return (
                  <div key={idx} className="bg-[#AC5757] rounded-lg p-4 text-white">
                    {/* Header */}
                    <div className="flex flex-col items-center mb-3">
                      <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center mb-2">
                        <Icon size={20} className="text-white" />
                      </div>
                      <div className="text-lg font-bold text-center">{card.title}</div>
                    </div>
                    
                    {/* Progress Circle with Percentage */}
                    <div className="flex justify-center mb-3">
                      <div className="relative w-16 h-16">
                        <svg className="w-16 h-16 transform -rotate-90">
                          <circle
                            cx="32"
                            cy="32"
                            r="28"
                            stroke="rgba(255,255,255,0.2)"
                            strokeWidth="4"
                            fill="none"
                          />
                          <circle
                            cx="32"
                            cy="32"
                            r="28"
                            stroke="white"
                            strokeWidth="4"
                            fill="none"
                            strokeDasharray={`${2 * Math.PI * 28}`}
                            strokeDashoffset={`${2 * Math.PI * 28 * (1 - card.value / 100)}`}
                            className="transition-all duration-500"
                          />
                        </svg>
                        {/* Percentage Text in Center */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-white font-bold text-sm">{card.value}%</span>
                        </div>
                      </div>
                    </div>

                    {/* Sections - Compact */}
                    <div className="space-y-1">
                      {Object.entries(card.sections).map(([sectionName, items]) => (
                        <div key={sectionName}>
                          <h4 className="text-xs font-semibold mb-1 text-white/90">{sectionName}:</h4>
                          <div className="space-y-1">
                            {items.slice(0, 2).map((item, itemIdx) => {
                              const progress = item.hours !== undefined 
                                ? Math.round((item.hours / item.maxHours) * 100)
                                : item.attempts !== undefined 
                                  ? Math.round((item.attempts / item.maxAttempts) * 100)
                                  : 0;
                              
                              return (
                                <div key={itemIdx} className="text-xs">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-white/90 font-medium truncate flex-1 mr-1 text-xs">{item.name}</span>
                                    {item.hours !== undefined ? (
                                      <span className="text-white/70 font-semibold text-xs whitespace-nowrap">{item.hours}h</span>
                                    ) : item.attempts !== undefined ? (
                                      <span className="text-white/70 font-semibold text-xs whitespace-nowrap">{item.attempts}</span>
                                    ) : null}
                                  </div>
                                  <div className="w-full bg-white/20 rounded-full h-1">
                                    <div 
                                      className="bg-white h-1 rounded-full transition-all duration-500"
                                      style={{ width: `${Math.min(progress, 100)}%` }}
                                    ></div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                </div>
              );
            })}
          </div>
          </div>
          
          {/* Bottom Section - Recent Achievements (Full Width) */}
          <section>
            <h3 className={`text-2xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Recent Achievements</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recentAchievements.map((achievement, idx) => {
                const Icon = achievement.icon;
                return (
                  <div key={idx} className={`rounded-lg p-6 shadow-sm border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#AC5757] rounded-full flex items-center justify-center flex-shrink-0">
                        <Icon size={20} className="text-white" />
                      </div>
                    <div>
                        <h4 className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{achievement.title}</h4>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{achievement.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </section>

          {/* To-Do/Goals Section */}
          <section>
            <div className="flex items-center justify-between mb-6">
              <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Goals & To-Do</h3>
              <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                <span className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-full">
                  <CheckCircle2 size={16} className="text-green-600" />
                  AI-powered completion tracking
                </span>
              </div>
            </div>
            
            <div className={`rounded-2xl p-6 border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm`}>
              {/* Add New Todo */}
              <div className="mb-6">
                <div className="flex gap-3">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      value={newTodo}
                      onChange={(e) => setNewTodo(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addTodo()}
                      placeholder="Add a new goal or task..."
                      className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-[#AC5757] focus:border-transparent transition-all ${
                        isDarkMode 
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 hover:border-gray-500' 
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 hover:border-gray-400'
                      }`}
                    />
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <Target size={20} className={`${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                    </div>
                  </div>
                  <button
                    onClick={addTodo}
                    className="px-6 py-3 bg-[#AC5757] text-white rounded-lg hover:bg-[#8A4A4A] transition-all duration-200 flex items-center gap-2 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                  >
                    <Plus size={20} />
                    Add Goal
                  </button>
                </div>
              </div>

              {/* Todo List */}
              <div className="space-y-3">
                {todos.length === 0 ? (
                  <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <div className="w-20 h-20 bg-gradient-to-br from-[#AC5757] to-[#8A4A4A] rounded-full flex items-center justify-center mx-auto mb-4">
                      <Target size={32} className="text-white" />
                    </div>
                    <h4 className="text-lg font-semibold mb-2">No goals set yet</h4>
                    <p>Add your first goal above to start tracking your progress!</p>
                  </div>
                ) : (
                  todos.map((todo) => (
                    <div
                      key={todo.id}
                      className={`flex items-center gap-4 p-5 rounded-xl border transition-all duration-200 hover:shadow-md ${
                        isDarkMode ? 'bg-gray-700 border-gray-600 hover:border-gray-500' : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {/* Completion Status */}
                      <div className="flex-shrink-0">
                        {todo.completed ? (
                          <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                            <CheckCircle2 size={20} className="text-white" />
                          </div>
                        ) : (
                          <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center ${
                            isDarkMode ? 'border-gray-500 hover:border-[#AC5757]' : 'border-gray-300 hover:border-[#AC5757]'
                          } transition-colors`}>
                            <Circle size={16} className={`${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
                          </div>
                        )}
                      </div>

                      {/* Todo Content */}
                      <div className="flex-1">
                        {editingTodo === todo.id ? (
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={editText}
                              onChange={(e) => setEditText(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && saveEdit()}
                              className={`flex-1 px-3 py-2 rounded border focus:ring-2 focus:ring-[#AC5757] focus:border-transparent ${
                                isDarkMode 
                                  ? 'bg-gray-600 border-gray-500 text-white' 
                                  : 'bg-white border-gray-300 text-gray-900'
                              }`}
                              autoFocus
                            />
                            <button
                              onClick={saveEdit}
                              className="px-3 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                            >
                              Save
                            </button>
                            <button
                              onClick={cancelEdit}
                              className="px-3 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div>
                            <p className={`text-lg font-medium ${
                              todo.completed 
                                ? `line-through ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`
                                : `${isDarkMode ? 'text-white' : 'text-gray-900'}`
                            }`}>
                              {todo.text}
                            </p>
                            {todo.completed && (
                              <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                Completed by AI
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      {!todo.completed && editingTodo !== todo.id && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => startEdit(todo)}
                            className="p-2 rounded-lg bg-[#AC5757]/10 text-[#AC5757] hover:bg-[#AC5757]/20 transition-all duration-200 hover:scale-105"
                          >
                            <Edit3 size={16} />
                          </button>
                          <button
                            onClick={() => deleteTodo(todo.id)}
                            className="p-2 rounded-lg bg-red-100 text-red-500 hover:bg-red-200 transition-all duration-200 hover:scale-105"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>

              {/* AI Status */}
              <div 
                className={`mt-6 p-5 rounded-xl border ${isDarkMode ? 'bg-gray-800/30 border-red-500/30' : 'bg-gray-100/30 border-red-400/30'} transition-all duration-300`}
                style={{
                  boxShadow: (() => {
                    const rect = document.querySelector('.ai-status-container')?.getBoundingClientRect();
                    if (!rect) return '';
                    
                    const centerX = rect.left + rect.width / 2;
                    const centerY = rect.top + rect.height / 2;
                    const distance = Math.sqrt(
                      Math.pow(mousePosition.x - centerX, 2) + Math.pow(mousePosition.y - centerY, 2)
                    );
                    const maxDistance = 200;
                    const intensity = Math.max(0, 1 - distance / maxDistance);
                    
                    const glowSize = 15 + (intensity * 10);
                    const opacity = 0.1 + (intensity * 0.2);
                    
                    return isDarkMode 
                      ? `0 0 ${glowSize}px rgba(239, 68, 68, ${opacity}), 0 0 ${glowSize * 1.5}px rgba(239, 68, 68, ${opacity * 0.5})`
                      : `0 0 ${glowSize}px rgba(248, 113, 113, ${opacity}), 0 0 ${glowSize * 1.5}px rgba(248, 113, 113, ${opacity * 0.5})`;
                  })()
                }}
              >
                <div className="ai-status-container">
                  <p className={`font-semibold text-lg mb-2 ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
                    AI Completion Tracking Active
                  </p>
                  <p className={`text-sm ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                    Our AI analyzes your progress and automatically marks completed tasks based on course completion, assignment submissions, and learning milestones.
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>

      </div>

      {/* Navigation Modal */}
      <NavigationModal isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
    </div>
  );
};

export default Progress;

